import React, { FC, useCallback, useEffect } from "react";
import { IconToggle, IOption, Menu } from "@evergis/ui";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { FeatureLayerServiceInfoDc, GeometryType, Permissions } from "@evergis/api";
import { PopupWithCustomBlank } from "../../styled";
import { LayerOpacity } from "../LayerOpacity/LayerOpacity";
import { MenuOptionRender } from "./MenuOptionRender";
import { LayerOpacityContainer, MenuScrollContainer, MenuWithOpacityContainer } from "./styled";
import { getUsername } from "../../../../ducks/user";
import { useToggle } from "../../../../hooks/useToggle";
import { useLayerSelectOptions } from "../../hooks/useLayerSelectOptions";
import { useMenuOptions } from "../../hooks/useMenuOptions";
import { useIsShared } from "../../../../hooks/api/useIsShared";
import { useProjectInfo } from "../../../../ducks/project/hooks";
import { ListMenuProps } from "./types";

export const ListMenu: FC<ListMenuProps> = ({
  isDragging,
  name,
  opacity,
  isVisibleItem,
  loading,
  layer,
  table,
  status,
  hasRasters,
  isGroup,
}) => {
  const { t } = useTranslation("common");
  const selectOption = useLayerSelectOptions(name, !layer);
  const [isVisiblePopup, togglePopup] = useToggle(false);
  const isShared = useIsShared();
  const [{ permissions, name: projectName, owner }] = useProjectInfo();
  const username = useSelector(getUsername);
  const isOwner = owner && owner === username && !!projectName;
  const isConfigurable = permissions?.includes(Permissions.Configure);

  const menuOptions = useMenuOptions(
    layer as FeatureLayerServiceInfoDc,
    table,
    status,
    loading,
    isVisibleItem,
    hasRasters,
    isGroup,
  );

  useEffect(() => {
    if (isDragging && isVisiblePopup) {
      togglePopup();
    }
  }, [isVisiblePopup, isDragging]);

  const handleOptionSelect = useCallback(
    (items: IOption[]) => {
      selectOption(items);
      isVisiblePopup && togglePopup();
    },
    [isVisiblePopup, selectOption, togglePopup],
  );

  return (
    <PopupWithCustomBlank
      isVisible={isVisiblePopup}
      onRequestClose={togglePopup}
      anchor={ref => (
        <IconToggle
          kind={"menu_vertical"}
          disabled={loading || (isGroup && isShared)}
          innerRef={ref}
          onClick={e => {
            e.stopPropagation();
            document.body.click();
            togglePopup();
          }}
        />
      )}
    >
      <MenuWithOpacityContainer>
        <MenuScrollContainer>
          {isVisiblePopup && (
            <Menu
              options={menuOptions.map(option => ({
                ...option,
                text: <MenuOptionRender item={option} layer={layer} table={table} />,
              }))}
              selectOption={handleOptionSelect}
              showAmount={6}
              withPadding
              showMoreWord={t("more")}
            />
          )}
        </MenuScrollContainer>
        {!isGroup &&
          status !== "layerError" &&
          !(layer?.type === "PostgresLayerService" && layer?.geometryType === GeometryType.Unknown) &&
          isVisibleItem &&
          (isOwner || isConfigurable) && (
            <LayerOpacityContainer>
              <LayerOpacity layerName={name} opacity={+(opacity * 100).toFixed(2)} />
            </LayerOpacityContainer>
          )}
      </MenuWithOpacityContainer>
    </PopupWithCustomBlank>
  );
};
